/**
 * Runtime API base for the supplier portal (multi-tenant SaaS migration, D5).
 *
 * CRA inlines `process.env.*` at build time, so a single fleet-wide build cannot vary
 * `REACT_APP_API_URL` per tenant (see D14 — one build serves every tenant). Fleet tenants are
 * served at `hisbah.<tenant-domain>`; on those hosts the API lives at the tenant's own domain
 * (the hostname minus the leading `hisbah.` label) — the pool-pinned Lumen worker there already
 * knows its tenant, so this needs zero new server-side resolution code. Non-fleet hosts (staging,
 * `dumyah.com`, localhost) keep the existing build-time env var untouched.
 */

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1', '0.0.0.0'])

/** True when `url`'s host is the browsing machine itself — a local-dev-only default. */
function isLocalhostUrl(url: string): boolean {
  try {
    return LOCAL_HOSTNAMES.has(new URL(url).hostname)
  } catch {
    return false
  }
}

/** Pure + synchronous, safe to call repeatedly (e.g. from tests) — reads `window.location` live. */
export function runtimeApiBase(): string {
  try {
    const hostname = window.location.hostname
    // The supplier portal is served at `hisbah.<domain>` AND the `hisbeh.<domain>` spelling (both are
    // the same "Hisbeh" brand; both 7-char leading labels). Match either.
    if (hostname.startsWith('hisbah.') || hostname.startsWith('hisbeh.')) {
      // The tenant's storefront (and its /api) lives at the CANONICAL host www.<domain>. diwan forms
      // the supplier host as `hisb{a,e}h.<canonical-without-www>` (SupplierWebProvisioner strips the www),
      // so prepending www. restores the exact canonical. Targeting the bare apex <domain> instead would
      // hit its 301 redirect to www — which browsers reject on a credentialed CORS fetch (the redirect
      // response must itself pass CORS, and even then cross-origin credentialed redirects are unreliable).
      return 'https://www.' + hostname.slice('hisbah.'.length)
    }

    const envUrl = process.env.REACT_APP_API_URL
    if (envUrl) {
      // Running locally (dev server on localhost/127.0.0.1) with a localhost API target is the
      // legitimate `yarn start` case — keep it. Anywhere else, a localhost API URL can only be
      // this repo's committed .env dev default leaking into the ONE build shared by every tenant
      // (see deploy-fleet.yml) — never silently ship that; every tenant's API calls would
      // otherwise point back at whichever machine happened to load the page. Fail safe to
      // same-origin instead.
      if (LOCAL_HOSTNAMES.has(hostname) || !isLocalhostUrl(envUrl)) {
        return envUrl
      }
      // eslint-disable-next-line no-console
      console.error(
        `[apiBase] REACT_APP_API_URL resolved to a localhost URL (${envUrl}) on host ` +
          `"${hostname}" — falling back to same-origin (${window.location.origin}).`
      )
      return window.location.origin
    }
  } catch {
    /* no window (SSR/non-browser test context) */
  }
  return process.env.REACT_APP_API_URL || ''
}

/** Computed once at module load. Every module-scope `API_URL` constant should import this instead. */
export const API_URL = runtimeApiBase()
